type ConnectionState = "online" | "reconnecting";
type Listener = (state: ConnectionState) => void;

const PING_URL = "/v1/health";
const PING_INTERVAL_MS = 3000;
const RECONNECT_THRESHOLD_MS = 1500;

let state: ConnectionState = "online";
let listeners: Listener[] = [];
let pingTimer: ReturnType<typeof setInterval> | null = null;
let offlineSince: number | null = null;
let bannerVisible = false;

function notify() {
  for (const fn of listeners) fn(state);
}

function startPinging() {
  if (pingTimer !== null) return;
  pingTimer = setInterval(async () => {
    try {
      const res = await fetch(PING_URL, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
      });
      if (res.ok) {
        markOnline();
      } else {
        maybeMarkReconnecting();
      }
    } catch {
      maybeMarkReconnecting();
    }
  }, PING_INTERVAL_MS);
}

function stopPinging() {
  if (pingTimer !== null) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
}

function maybeMarkReconnecting() {
  if (offlineSince === null) {
    offlineSince = Date.now();
  }
  const elapsed = Date.now() - offlineSince;
  if (elapsed >= RECONNECT_THRESHOLD_MS && state !== "reconnecting") {
    state = "reconnecting";
    bannerVisible = true;
    notify();
  }
}

function markOnline() {
  offlineSince = null;
  stopPinging();
  if (state !== "online") {
    state = "online";
    notify();
    setTimeout(() => {
      bannerVisible = false;
      notify();
    }, 800);
  } else {
    bannerVisible = false;
  }
}

export function reportRequestFailure(status?: number) {
  const isServerError = status === undefined || status === 0 || status === 502 || status === 503 || status === 504;
  if (isServerError) {
    maybeMarkReconnecting();
    startPinging();
  }
}

export function reportRequestSuccess() {
  if (state === "reconnecting" || offlineSince !== null) {
    markOnline();
  }
}

export function getConnectionState(): ConnectionState {
  return state;
}

export function isBannerVisible(): boolean {
  return bannerVisible;
}

export function subscribeToConnectionState(fn: Listener): () => void {
  listeners.push(fn);
  return () => {
    listeners = listeners.filter(l => l !== fn);
  };
}
